<template></template>

<script lang="ts">
import * as Sentry from '@sentry/vue';
import type { PropType } from 'vue';
import { defineComponent, nextTick, watch } from 'vue';
import type { IntegrationEspEloquaModel } from './Model';
import useAxios from '@/src/hooks/useAxios';
import { getCurrentUrl, getQueryParams } from '@/src/utilities/Url';
import type { ReplacementTags } from '@/src/store/campaign';
import { useCampaignStore } from '@/src/store/campaign';

interface EloquaPulApiResponseField {
  alias: string;
  value: string | number | boolean;
}

interface EloquaPulApiResponse {
  fields?: {
    [key: number]: EloquaPulApiResponseField;
  };
}

export default defineComponent({
  name: 'integration-esp-eloqua',
  props: {
    model: {
      type: Object as PropType<IntegrationEspEloquaModel>,
      required: true
    }
  },
  setup(props) {
    const campaignStore = useCampaignStore();

    let purlProcessed = false;
    let readyPromiseResolve: (() => void) | undefined;
    const readyPromise = new Promise<void>((resolve) => {
      readyPromiseResolve = resolve;
    });

    const processPurl = async () => {
      if (purlProcessed) {
        return;
      }

      purlProcessed = true;

      const params = getQueryParams();

      let purl = typeof params.purl !== 'undefined' && params.purl ? params.purl : undefined;
      let token = typeof params.token !== 'undefined' && params.token ? params.token : undefined;

      if (Array.isArray(purl)) {
        purl = purl.shift();
      }

      if (Array.isArray(token)) {
        token = token.shift();
      }

      // If we have purl feature enabled & purl exists in query parameters... continue.
      if (
        props.model.state.purl &&
        (typeof purl !== 'undefined' || typeof token !== 'undefined') &&
        props.model.state.endpoint
      ) {
        const { fetchData } = useAxios<EloquaPulApiResponse>(props.model.state.endpoint, {
          purl: purl || token,
          is_token: !!token
        });

        try {
          const response = await fetchData();

          // If user was located & fields exists. Then we loop over the fields and create replacement tags for each field.
          // This is so we can use the fields as replacement tag in the content
          // - but also to pre-fill information in the registration addon.
          if (response.fields) {
            const replacementTags: ReplacementTags = {};

            for (const fieldId in response.fields) {
              if (response.fields[Number(fieldId)].alias && response.fields[Number(fieldId)]) {
                campaignStore.model?.formFields.forEach((field) => {
                  if (field.id === fieldId && response.fields && response.fields[Number(fieldId)]) {
                    field.state.value = field.parseStringValue(response.fields[Number(fieldId)].value.toString());
                    field.redoParse();
                  }
                });

                // Rest is for content replacement.
                replacementTags[Number(fieldId)] = response.fields[Number(fieldId)].value.toString();
                replacementTags[response.fields[Number(fieldId)].alias] =
                  response.fields[Number(fieldId)].value.toString();
              }
            }

            campaignStore.addReplacementTags(replacementTags);
          }
        } catch (e) {
          // Ignore network errors & just display campaign.
          Sentry.captureException(e);
        }
      }

      if (readyPromiseResolve) {
        readyPromiseResolve();
      }
    };

    const isDemo = getCurrentUrl().includes('/campaign/view/demo');

    // Wait for purl to be ready before entering, timeout after 2 seconds and show campaign anyway.
    if (props.model.state.purl && props.model.state.purlWait && !isDemo) {
      const watcher = watch(
        () => {
          const queryParams = getQueryParams();

          return queryParams.purl ?? queryParams.token ?? undefined;
        },
        (newValue) => {
          if (newValue) {
            processPurl();

            nextTick(() => {
              watcher();
            });
          }
        },
        {
          immediate: true
        }
      );

      window.setTimeout(() => {
        watcher();
        processPurl();
      }, 2000);
    } else if (props.model.state.purl) {
      processPurl();
    } else if (readyPromiseResolve) {
      readyPromiseResolve();
    }

    return {
      onBeforeEnter: async () => {
        await readyPromise;
      }
    };
  }
});
</script>
